import api from './api';
import { TradingAgent, AgentCreate, AgentUpdate, AgentExecution } from '../types/agent';

const API_BASE_URL = '';

export const getAgents = async (): Promise<TradingAgent[]> => {
  const res = await api.get(`${API_BASE_URL}/agents`);
  return res.data;
};

export const getAgent = async (agentId: string): Promise<TradingAgent> => {
  const res = await api.get(`${API_BASE_URL}/agents/${agentId}`);
  return res.data;
};

export const createAgent = async (config: AgentCreate): Promise<TradingAgent> => {
  const res = await api.post(`${API_BASE_URL}/agents`, config);
  return res.data;
};

export const updateAgent = async (agentId: string, updates: AgentUpdate): Promise<TradingAgent> => {
  const res = await api.put(`${API_BASE_URL}/agents/${agentId}`, updates);
  return res.data;
};

export const deleteAgent = async (agentId: string): Promise<void> => {
  await api.delete(`${API_BASE_URL}/agents/${agentId}`);
};

export const updateAgentStatus = async (
  agentId: string,
  status: 'idle' | 'running' | 'paused',
): Promise<void> => {
  await api.put(`${API_BASE_URL}/agents/${agentId}/status`, null, { params: { status } });
};

export const executeAgent = async (
  agentId: string,
  inputData: string,
  context?: Record<string, unknown>,
  model?: string,
): Promise<AgentExecution> => {
  const res = await api.post(`${API_BASE_URL}/agents/${agentId}/execute`, {
    input_data: inputData,
    context: context ?? {},
    model: model ?? null,
  });
  return res.data;
};

export const getAgentHistory = async (
  agentId: string,
  limit = 20,
): Promise<AgentExecution[]> => {
  const res = await api.get(`${API_BASE_URL}/agents/${agentId}/history`, {
    params: { limit },
  });
  return res.data;
};

export interface AgentPromptPreview {
  agent_id: string;
  agent_name: string;
  system_prompt: string;
  is_override: boolean;
  agent_type: string;
}

export const previewAgentPrompt = async (agentId: string): Promise<AgentPromptPreview> => {
  const res = await api.get(`${API_BASE_URL}/agents/${agentId}/preview-prompt`);
  return res.data;
};

export interface AgentConnectionDataPreview {
  agent_id: string;
  agent_name: string;
  agent_type: string;
  data: Record<string, unknown>;
}

export const previewConnectionData = async (agentId: string): Promise<AgentConnectionDataPreview> => {
  const res = await api.get(`${API_BASE_URL}/agents/${agentId}/connection-data`);
  return res.data;
};

export const getActiveWindows = async (): Promise<Array<{
  agent_id: string;
  session_id: string | null;
  is_running: boolean;
  is_paused: boolean;
  is_continuous: boolean;
}>> => {
  const res = await api.get(`${API_BASE_URL}/agents/windows/active`);
  return res.data;
};

export const stopAgentWindow = async (agentId: string): Promise<void> => {
  await api.post(`${API_BASE_URL}/agents/${agentId}/window/stop`);
};

// ---------------------------------------------------------------------------
// Continuous agent control
// ---------------------------------------------------------------------------

export const startAgent = async (agentId: string): Promise<{ started: boolean; status: string }> => {
  const res = await api.post(`${API_BASE_URL}/agents/${agentId}/start`);
  return res.data;
};

export const pauseAgent = async (agentId: string): Promise<{ paused: boolean; status: string }> => {
  const res = await api.post(`${API_BASE_URL}/agents/${agentId}/pause`);
  return res.data;
};

export const resumeAgent = async (agentId: string): Promise<{ resumed: boolean; status: string }> => {
  const res = await api.post(`${API_BASE_URL}/agents/${agentId}/resume`);
  return res.data;
};

export const stopAgent = async (agentId: string): Promise<{ stopped: boolean; status: string }> => {
  const res = await api.post(`${API_BASE_URL}/agents/${agentId}/stop`);
  return res.data;
};
